import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Users, MapPin, Briefcase, ArrowRight, Upload,
  CheckCircle, Mail, FileText, X, Star, Globe, Heart
} from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Careers.css';
import { CareersHero } from '../components/PageHeroes';
import { sendFormData, ENDPOINTS } from '../utils/sendEmail';

const Careers = () => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    role: '', experience: '', linkedIn: '', message: ''
  });
  const [cvFile, setCvFile]     = useState(null);
  const [status, setStatus]     = useState('idle');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const perks = [
    { icon: Users,     title: 'Collaborative Culture', description: 'Work with talented, driven individuals who genuinely care about the product and each other.', accent: 'blue' },
    { icon: Briefcase, title: 'Career Growth',         description: 'Structured development plans, mentorship, and clear progression paths for every team member.', accent: 'green' },
    { icon: Globe,     title: 'Flexible & Remote',     description: 'Work from anywhere in the UK with flexible hours that fit your life, not the other way around.', accent: 'blue' },
    { icon: Heart,     title: 'Great Benefits',        description: 'Competitive salary, private healthcare, pension, 25 days holiday, and regular team events.',    accent: 'green' },
    { icon: Star,      title: 'Impactful Work',        description: 'Your work directly affects thousands of UK businesses and their employees every single day.',   accent: 'blue' },
    { icon: MapPin,    title: 'London HQ',             description: 'Beautiful office in central London for those who prefer in-person collaboration and team days.',  accent: 'green' }
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFile = (file) => {
    if (!file) return;
    const allowed = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowed.includes(file.type)) {
      alert('Please upload a PDF or Word document.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5MB.');
      return;
    }
    setCvFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cvFile) {
      alert('Please attach your CV before submitting.');
      return;
    }
    setStatus('sending');

    const data = new FormData();
    data.append('firstName',  form.firstName);
    data.append('lastName',   form.lastName);
    data.append('email',      form.email);
    data.append('phone',      form.phone);
    data.append('role',       form.role);
    data.append('experience', form.experience);
    data.append('linkedIn',   form.linkedIn || 'Not provided');
    data.append('message',    form.message  || 'No cover note provided.');
    data.append('cv',         cvFile, cvFile.name);

    try {
      await sendFormData(ENDPOINTS.CAREERS, data);
    } catch (err) {
      // If it's a network/CORS error but email still delivered, show success
      if (err.message === 'Failed to fetch' || err instanceof TypeError) {
        setStatus('success');
        setForm({ firstName: '', lastName: '', email: '', phone: '', role: '', experience: '', linkedIn: '', message: '' });
        setCvFile(null);
        return;
      }
      console.error('Careers error:', err);
      setStatus('error');
      return;
    }

    setStatus('success');
    setForm({ firstName: '', lastName: '', email: '', phone: '', role: '', experience: '', linkedIn: '', message: '' });
    setCvFile(null);
  };

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.1 } }
  };
  const itemVariants = {
    hidden:  { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <motion.div className="careers-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <CareersHero />

      {/* ===== PERKS ===== */}
      <section className="careers-section">
        <div className="careers-container">
          <motion.div className="careers-section-header" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <span className="careers-eyebrow">Life at Effective Global Staff</span>
            <h2>Why work with us</h2>
            <p>We build a workplace where talented people do the best work of their careers.</p>
          </motion.div>
          <motion.div className="careers-perks-grid" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {perks.map((perk, idx) => {
              const Icon = perk.icon;
              return (
                <motion.div key={idx} className={`careers-perk-card accent-${perk.accent}`} variants={itemVariants} whileHover={{ y: -6 }}>
                  <div className="careers-perk-icon">
                    <Icon size={22} />
                  </div>
                  <h3>{perk.title}</h3>
                  <p>{perk.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== APPLICATION FORM ===== */}
      <section className="careers-section careers-section-alt">
        <div className="careers-container">
          <motion.div className="careers-section-header" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <span className="careers-eyebrow">Open application</span>
            <h2>Apply to join our team</h2>
            <p>No specific role listed that fits? Send us your CV anyway — we hire for talent, not just vacancies.</p>
          </motion.div>

          <motion.div className="careers-form-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="careers-form-header">
              <FileText size={20} className="careers-form-header-icon" />
              <h3>Your application</h3>
            </div>

            {status === 'success' ? (
              <div className="careers-success">
                <CheckCircle size={44} className="careers-success-icon" />
                <h3>Application received!</h3>
                <p>Thank you for your interest. Our team will review your details and be in touch within 3 business days. Please also email your CV directly to <strong>thomas@effectivestaff.co.uk</strong> with your name as the subject.</p>
              </div>
            ) : (
              <form className="careers-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First name *</label>
                    <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="Jane" required />
                  </div>
                  <div className="form-group">
                    <label>Last name *</label>
                    <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Smith" required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email address *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="jane@email.com" required />
                  </div>
                  <div className="form-group">
                    <label>Phone number *</label>
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="+44 7700 900000" required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role you are applying for *</label>
                    <input name="role" value={form.role} onChange={handleChange} placeholder="e.g. React Developer, Payroll Specialist..." required />
                  </div>
                  <div className="form-group">
                    <label>Years of experience *</label>
                    <select name="experience" value={form.experience} onChange={handleChange} required>
                      <option value="">Select range</option>
                      <option value="0-1 years">0 - 1 years</option>
                      <option value="1-3 years">1 - 3 years</option>
                      <option value="3-5 years">3 - 5 years</option>
                      <option value="5-10 years">5 - 10 years</option>
                      <option value="10+ years">10+ years</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>LinkedIn profile</label>
                  <input name="linkedIn" value={form.linkedIn} onChange={handleChange} placeholder="https://linkedin.com/in/your-profile" />
                </div>
                <div className="form-group">
                  <label>Cover note</label>
                  <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us a bit about yourself, why you want to join Effective Global Staff, and what you would bring to the team..." rows={4} />
                </div>

                {/* CV Upload */}
                <div className="form-group">
                  <label>
                    Upload CV * <span className="form-label-hint">(PDF or Word, max 5MB)</span>
                  </label>
                  <div
                    className={`cv-dropzone ${dragOver ? 'cv-dropzone-active' : ''} ${cvFile ? 'cv-dropzone-filled' : ''}`}
                    onClick={() => fileRef.current.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFile(e.target.files[0])}
                    />
                    {cvFile ? (
                      <div className="cv-file-selected">
                        <FileText size={18} className="cv-file-icon" />
                        <span>{cvFile.name}</span>
                        <button
                          type="button"
                          className="cv-remove-btn"
                          onClick={(e) => { e.stopPropagation(); setCvFile(null); }}
                        >
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <div className="cv-placeholder">
                        <Upload size={24} className="cv-upload-icon" />
                        <p>Drag and drop your CV here, or <span>browse files</span></p>
                        <small>PDF or Word document, max 5MB</small>
                      </div>
                    )}
                  </div>
                  <p className="form-label-hint cv-note">
                    Note: Please also email your CV directly to <strong>thomas@effectivestaff.co.uk</strong> to ensure we receive it.
                  </p>
                </div>

                {status === 'error' && (
                  <p className="form-error">Something went wrong. Please try again or email us directly.</p>
                )}

                <button type="submit" className="careers-submit-btn" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Submitting...' : (
                    <span className="careers-submit-content">
                      Submit application <ArrowRight size={15} />
                    </span>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="careers-cta-section">
        <div className="careers-cta-bg" />
        <div className="careers-container">
          <motion.div className="careers-cta-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="careers-cta-icon"><Mail size={26} /></div>
            <h2>Have questions before applying?</h2>
            <p>Reach out to our team and we will be happy to tell you more about life at Effective Global Staff.</p>
            <a href="mailto:thomas@effectivestaff.co.uk" className="careers-cta-btn">
              Email careers team
            </a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Careers;